<template>
  <q-card style="min-width: 600px; max-width: 80vw">
    <q-card-section class="row items-center q-pb-none">
      <div class="text-h6">🔗 {{ $t('url_constructor.title') }}</div>
      <q-space />
      <q-btn icon="close" flat round dense v-close-popup />
    </q-card-section>

    <q-card-section>
      <div class="text-body2 text-grey-6 q-mb-md">
        {{ $t('url_constructor.pixel_info', { pixel_id: pixel?.pixel_id, pixel_name: pixel?.pixel_name }) }}
      </div>

      <q-scroll-area style="height: 50vh" class="q-pr-md">
        <!-- Обязательные параметры -->
        <div class="text-subtitle2 text-weight-bold q-mb-sm text-negative">
          ✅ {{ $t('url_constructor.required_params') }}
        </div>

        <div class="row q-mb-sm items-start">
          <div class="col-6">
            <q-checkbox
                v-model="params.pixel_id.enabled"
                :label="`pixel_id`"
                disable
                checked
                dense
            />
          </div>
          <div class="col-6">
            <q-input
                v-model="params.pixel_id.value"
                dense
                outlined
                readonly
                :value="pixel?.pixel_id"
            />
          </div>
        </div>

        <!-- Основные параметры -->
        <div class="text-subtitle2 text-weight-bold q-mb-sm text-primary">
          🎯 {{ $t('url_constructor.main_params') }}
        </div>

        <div v-for="param in mainParams" :key="param.key" class="row q-mb-xs items-start">
          <div class="col-6">
            <q-checkbox
                v-model="params[param.key].enabled"
                :label="param.key"
                @update:model-value="updateUrl"
                dense
            />
            <div class="text-caption text-grey-6">{{ $t(`url_constructor.params.${param.key}`) }}</div>
          </div>
          <div class="col-6">
            <q-input
                v-model="params[param.key].value"
                dense
                outlined
                :placeholder="param.placeholder"
                :disable="!params[param.key].enabled"
                @update:model-value="updateUrl"
            />
          </div>
        </div>

        <!-- Технические параметры -->
        <div class="text-subtitle2 text-weight-bold q-mb-sm text-orange">
          🌍 {{ $t('url_constructor.technical_params') }}
        </div>

        <div v-for="param in technicalParams" :key="param.key" class="row q-mb-xs items-start">
          <div class="col-6">
            <q-checkbox
                v-model="params[param.key].enabled"
                :label="param.key"
                @update:model-value="updateUrl"
                dense
            />
            <div class="text-caption text-grey-6">{{ $t(`url_constructor.params.${param.key}`) }}</div>
          </div>
          <div class="col-6">
            <q-input
                v-model="params[param.key].value"
                dense
                outlined
                :placeholder="param.placeholder"
                :disable="!params[param.key].enabled"
                @update:model-value="updateUrl"
            />
          </div>
        </div>

        <!-- Данные события -->
        <div class="text-subtitle2 text-weight-bold q-mb-sm text-purple">
          📦 {{ $t('url_constructor.event_data_params') }}
        </div>

        <div v-for="param in eventDataParams" :key="param.key" class="row q-mb-xs items-start">
          <div class="col-6">
            <q-checkbox
                v-model="params[param.key].enabled"
                :label="param.key"
                @update:model-value="updateUrl"
                dense
            />
            <div class="text-caption text-grey-6">{{ $t(`url_constructor.params.${param.key}`) }}</div>
          </div>
          <div class="col-6">
            <q-input
                v-model="params[param.key].value"
                dense
                outlined
                :placeholder="param.placeholder"
                :disable="!params[param.key].enabled"
                @update:model-value="updateUrl"
            />
          </div>
        </div>
      </q-scroll-area>
    </q-card-section>

    <q-separator />

    <!-- Сгенерированный URL -->
    <q-card-section>
      <div class="text-subtitle1 text-weight-bold q-mb-md">🔗 {{ $t('url_constructor.generated_url') }}</div>

      <q-input
          v-model="generatedUrl"
          readonly
          outlined
          type="textarea"
          rows="3"
          class="q-mb-md"
          bg-color="grey-1"
      />

      <div class="row q-gutter-sm">
        <q-btn
            color="primary"
            icon="content_copy"
            :label="$t('url_constructor.copy_url')"
            @click="copyUrl"
        />
        <q-btn
            color="secondary"
            icon="open_in_new"
            :label="$t('url_constructor.test_url')"
            @click="testUrl"
            :disable="!generatedUrl"
        />
      </div>
    </q-card-section>

    <q-card-actions align="right">
      <q-btn flat :label="$t('common.close')" color="grey" @click="$emit('close')" />
    </q-card-actions>
  </q-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  pixel: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])
const $q = useQuasar()
const { t } = useI18n()

// Базовый URL для API
const baseUrl = window.location.origin

// Параметры для конструктора
const params = ref({
  pixel_id: { enabled: true, value: '' },
  event_name: { enabled: false, value: 'Lead' },
  email: { enabled: false, value: '' },
  phone: { enabled: false, value: '' },
  first_name: { enabled: false, value: '' },
  last_name: { enabled: false, value: '' },
  city: { enabled: false, value: '' },
  state: { enabled: false, value: '' },
  country: { enabled: false, value: '' },
  zip: { enabled: false, value: '' },
  dob: { enabled: false, value: '' },
  external_id: { enabled: false, value: '' },
  event_id: { enabled: false, value: '' },
  event_time: { enabled: false, value: '' },
  url: { enabled: false, value: '' },
  ip: { enabled: false, value: '' },
  action_source: { enabled: false, value: 'website' },
  client_ip_address: { enabled: false, value: '' },
  client_user_agent: { enabled: false, value: '' },
  fbc: { enabled: false, value: '' },
  fbp: { enabled: false, value: '' },
  content_name: { enabled: false, value: '' },
  content_category: { enabled: false, value: '' },
  content_type: { enabled: false, value: '' },
  content_ids: { enabled: false, value: '' },
  currency: { enabled: false, value: 'USD' },
  value: { enabled: false, value: '' },
  num_items: { enabled: false, value: '' }
})

// Конфигурация параметров (теперь только ключи и плейсхолдеры)
const mainParams = [
  { key: 'event_name', placeholder: 'Lead' },
  { key: 'email', placeholder: 'user@example.com' },
  { key: 'phone', placeholder: '+1234567890' },
  { key: 'first_name', placeholder: 'John' },
  { key: 'last_name', placeholder: 'Doe' },
  { key: 'city', placeholder: 'New York' },
  { key: 'state', placeholder: 'NY' },
  { key: 'country', placeholder: 'US' },
  { key: 'zip', placeholder: '10001' },
  { key: 'dob', placeholder: '19900101' },
  { key: 'external_id', placeholder: 'user_12345' },
  { key: 'event_id', placeholder: 'event_12345' },
  { key: 'event_time', placeholder: '2024-01-15 10:30:00' },
  { key: 'url', placeholder: 'https://mysite.com/landing' }
]

const technicalParams = [
  { key: 'action_source', placeholder: 'website' },
  { key: 'ip', placeholder: '192.168.1.1' },
  { key: 'client_ip_address', placeholder: '192.168.1.1' },
  { key: 'client_user_agent', placeholder: 'Mozilla/5.0...' },
  { key: 'fbc', placeholder: 'fb.1.1234567890.abcdef' },
  { key: 'fbp', placeholder: 'fb.1.1234567890.123456789' }
]

const eventDataParams = [
  { key: 'content_name', placeholder: 'Lead Generation' },
  { key: 'content_category', placeholder: 'lead' },
  { key: 'content_type', placeholder: 'product' },
  { key: 'content_ids', placeholder: 'product_1,product_2' },
  { key: 'currency', placeholder: 'USD' },
  { key: 'value', placeholder: '99.99' },
  { key: 'num_items', placeholder: '1' }
]

// Сгенерированный URL
const generatedUrl = computed(() => {
  const params_array = []

  Object.keys(params.value).forEach(key => {
    const param = params.value[key]
    if (param.enabled && param.value) {
      params_array.push(`${key}=${param.value}`)
    }
  })

  return `${baseUrl}/convert?${params_array.join('&')}`
})

// Методы
const updateUrl = () => {
  // URL обновляется автоматически через computed
}

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(generatedUrl.value)
    $q.notify({
      type: 'positive',
      message: t('url_constructor.url_copied'),
      position: 'top'
    })
  } catch (error) {
    // Fallback для старых браузеров
    const textArea = document.createElement('textarea')
    textArea.value = generatedUrl.value
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)

    $q.notify({
      type: 'positive',
      message: t('url_constructor.url_copied'),
      position: 'top'
    })
  }
}

const testUrl = () => {
  window.open(generatedUrl.value, '_blank')
}

// Инициализация
onMounted(() => {
  if (props.pixel) {
    params.value.pixel_id.value = props.pixel.pixel_id
  }
})
</script>

<style scoped>
.q-checkbox {
  margin-bottom: 4px;
}
</style>